// ============================================================
// Learning Storage Service (localStorage)
// Tracks granular progress: section index, subpage, etc.
// ============================================================

const STORAGE_PREFIX = "eap_learning_";

export interface LearningProgress {
  moduleId: string;
  currentSectionIndex: number;
  currentSectionId: string;
  currentSubPageIndex: number;
  sectionsCompleted: number[];
  totalSections: number;
  lastUpdate: number;
  contentVersion: string;
  startedAt: number;
}

// ============================================================
// Core Methods
// ============================================================

/**
 * Generate localStorage key for a module
 */
const getStorageKey = (moduleId: string): string => {
  return `${STORAGE_PREFIX}${moduleId}`;
};

/**
 * Save learning progress to localStorage
 */
export const saveLearningProgress = (progress: LearningProgress): void => {
  try {
    const key = getStorageKey(progress.moduleId);
    const data = {
      ...progress,
      lastUpdate: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error("Error saving learning progress:", error);
  }
};

/**
 * Load learning progress from localStorage
 * Returns null if not found or invalid
 */
export const loadLearningProgress = (
  moduleId: string
): LearningProgress | null => {
  try {
    const key = getStorageKey(moduleId);
    const data = localStorage.getItem(key);

    if (!data) {
      return null;
    }

    const parsed = JSON.parse(data) as LearningProgress;

    // Validate required fields
    if (!parsed.moduleId || parsed.currentSectionIndex === undefined) {
      console.warn("Invalid learning progress data, removing:", moduleId);
      removeLearningProgress(moduleId);
      return null;
    }

    return parsed;
  } catch (error) {
    console.error("Error loading learning progress:", error);
    return null;
  }
};

/**
 * Remove learning progress from localStorage
 * Call this when learning content is completed
 */
export const removeLearningProgress = (moduleId: string): void => {
  try {
    const key = getStorageKey(moduleId);
    localStorage.removeItem(key);
  } catch (error) {
    console.error("Error removing learning progress:", error);
  }
};

/**
 * Check if progress exists for a module
 */
export const hasLearningProgress = (moduleId: string): boolean => {
  const key = getStorageKey(moduleId);
  return localStorage.getItem(key) !== null;
};

// ============================================================
// Progress Calculation
// ============================================================

/**
 * Get progress percentage for a module (0-100)
 */
export const getProgressPercentage = (moduleId: string): number => {
  const progress = loadLearningProgress(moduleId);

  if (!progress || progress.totalSections === 0) {
    return 0;
  }

  return Math.round(
    (progress.sectionsCompleted.length / progress.totalSections) * 100
  );
};

/**
 * Check if all sections are completed
 */
export const isAllSectionsCompleted = (moduleId: string): boolean => {
  const progress = loadLearningProgress(moduleId);

  if (!progress) {
    return false;
  }

  return progress.sectionsCompleted.length >= progress.totalSections;
};

// ============================================================
// Progress Initialization & Updates
// ============================================================

/**
 * Initialize new learning progress for a module
 */
export const initializeLearningProgress = (
  moduleId: string,
  totalSections: number,
  firstSectionId: string,
  contentVersion: string = "v1.0"
): LearningProgress => {
  const progress: LearningProgress = {
    moduleId,
    currentSectionIndex: 0,
    currentSectionId: firstSectionId,
    currentSubPageIndex: 0,
    sectionsCompleted: [],
    totalSections,
    lastUpdate: Date.now(),
    contentVersion,
    startedAt: Date.now(),
  };

  saveLearningProgress(progress);
  return progress;
};

/**
 * Update current position (section and subpage)
 */
export const updateCurrentPosition = (
  moduleId: string,
  sectionIndex: number,
  sectionId: string,
  subPageIndex: number
): void => {
  const progress = loadLearningProgress(moduleId);

  if (!progress) {
    console.warn("No progress found for module:", moduleId);
    return;
  }

  progress.currentSectionIndex = sectionIndex;
  progress.currentSectionId = sectionId;
  progress.currentSubPageIndex = subPageIndex;

  saveLearningProgress(progress);
};

/**
 * Mark a section as completed
 */
export const markSectionCompleted = (
  moduleId: string,
  sectionIndex: number
): void => {
  const progress = loadLearningProgress(moduleId);

  if (!progress) {
    console.warn("No progress found for module:", moduleId);
    return;
  }

  // Add to completed if not already there
  if (!progress.sectionsCompleted.includes(sectionIndex)) {
    progress.sectionsCompleted.push(sectionIndex);
    progress.sectionsCompleted.sort((a, b) => a - b); // Keep sorted
  }

  saveLearningProgress(progress);
};

/**
 * Move to next subpage within a section
 * Returns true if moved, false if at last subpage
 */
export const moveToNextSubPage = (
  moduleId: string,
  totalSubPagesInSection: number
): boolean => {
  const progress = loadLearningProgress(moduleId);

  if (!progress) {
    return false;
  }

  if (progress.currentSubPageIndex < totalSubPagesInSection - 1) {
    progress.currentSubPageIndex++;
    saveLearningProgress(progress);
    return true;
  }

  return false; // At last subpage
};

/**
 * Move to next section
 * Marks current section as completed and resets subpage to 0
 */
export const moveToNextSection = (
  moduleId: string,
  nextSectionId: string
): boolean => {
  const progress = loadLearningProgress(moduleId);

  if (!progress) {
    return false;
  }

  // Mark current section as completed
  markSectionCompleted(moduleId, progress.currentSectionIndex);

  // Check if there's a next section
  if (progress.currentSectionIndex < progress.totalSections - 1) {
    progress.currentSectionIndex++;
    progress.currentSectionId = nextSectionId;
    progress.currentSubPageIndex = 0;
    saveLearningProgress(progress);
    return true;
  }

  return false; // At last section
};

// ============================================================
// Validation
// ============================================================

/**
 * Validate saved progress against current content structure
 * Returns validated position or fallback to start
 */
export const validateAndGetPosition = (
  moduleId: string,
  sections: Array<{ id: string; subPages?: number }>
): { sectionIndex: number; subPageIndex: number; isValid: boolean } => {
  const progress = loadLearningProgress(moduleId);

  // No saved progress - start from beginning
  if (!progress) {
    return { sectionIndex: 0, subPageIndex: 0, isValid: false };
  }

  // Check if saved section index is valid
  if (progress.currentSectionIndex < sections.length) {
    const section = sections[progress.currentSectionIndex];

    // Verify section ID matches (content hasn't changed)
    if (section.id === progress.currentSectionId) {
      return {
        sectionIndex: progress.currentSectionIndex,
        subPageIndex: progress.currentSubPageIndex,
        isValid: true,
      };
    }

    // Section ID doesn't match - try to find by ID
    const foundIndex = sections.findIndex(
      (s) => s.id === progress.currentSectionId
    );
    if (foundIndex !== -1) {
      // Found at different index - update and return
      updateCurrentPosition(moduleId, foundIndex, progress.currentSectionId, 0);
      return { sectionIndex: foundIndex, subPageIndex: 0, isValid: true };
    }
  }

  // Content structure changed - fallback to start
  console.warn("Content structure changed, resetting progress for:", moduleId);
  removeLearningProgress(moduleId);
  return { sectionIndex: 0, subPageIndex: 0, isValid: false };
};

// ============================================================
// Utility
// ============================================================

/**
 * Get all learning progress keys from localStorage
 * Useful for debugging or clearing all progress
 */
export const getAllLearningProgressKeys = (): string[] => {
  const keys: string[] = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(STORAGE_PREFIX)) {
      keys.push(key);
    }
  }

  return keys;
};

/**
 * Clear all learning progress from localStorage
 * Use with caution - mainly for debugging
 */
export const clearAllLearningProgress = (): void => {
  const keys = getAllLearningProgressKeys();
  keys.forEach((key) => localStorage.removeItem(key));
};

// ============================================================
// Export as object for easier imports
// ============================================================

export const learningStorageService = {
  // Core
  saveLearningProgress,
  loadLearningProgress,
  removeLearningProgress,
  hasLearningProgress,

  // Progress calculation
  getProgressPercentage,
  isAllSectionsCompleted,

  // Initialization & updates
  initializeLearningProgress,
  updateCurrentPosition,
  markSectionCompleted,
  moveToNextSubPage,
  moveToNextSection,

  // Validation
  validateAndGetPosition,

  // Utility
  getAllLearningProgressKeys,
  clearAllLearningProgress,
};

export default learningStorageService;
